import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { buildOrganizationJsonLd, buildSoftwareApplicationJsonLd } from '../components/SEO';
import EnterpriseContactModal from '../components/EnterpriseContactModal';
import LandingLoginModal from '../components/landing/LandingLoginModal';
import LandingForgotModal from '../components/landing/LandingForgotModal';
import LandingRegisterModal from '../components/landing/LandingRegisterModal';
import {
  FooterSection,
  LandingHero,
  LandingHowItWorks,
  LandingNav,
  LandingPricing,
  TrustBanner,
} from '../components/landing-full';
import {
  VendorMarketplaceSection,
  TestimonialsSection,
  FAQSection,
  FinalCTASection,
  LandingFeatures,
  LandingBenefits,
  LandingPractices,
} from '../components/landing';

const orgJsonLd = buildOrganizationJsonLd();
const appJsonLd = buildSoftwareApplicationJsonLd();

const LandingNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { i18n } = useTranslation();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerRole, setRegisterRole] = useState('business');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);

  // !loading is the only guard needed here. It prevents the redirect from firing
  // during session restore, which was the original cause of issue #323 ("forgot
  // modal flash & bump to dashboard"). Gating the openX callbacks on top of this
  // swallowed user clicks while loading was true and produced the "click does
  // nothing / bumped back to login" symptom — most visible in French because
  // toggling ?lang=fr re-renders right at the auth-restore window.
  React.useEffect(() => {
    if (!loading && user && ['/landing-page', '/en/landing-page'].includes(location.pathname)) {
      if (user.role === 'admin') navigate('/admin/dashboard');
      else navigate('/dashboard');
    }
  }, [user, loading, navigate, location.pathname]);

  const openLogin = () => { setShowRegisterModal(false); setShowForgotModal(false); setShowLoginModal(true); };
  const openRegister = () => { setRegisterRole('business'); setShowLoginModal(false); setShowForgotModal(false); setShowRegisterModal(true); };
  const openRegisterAs = (role) => { setRegisterRole(role); setShowLoginModal(false); setShowForgotModal(false); setShowRegisterModal(true); };
  const openForgot = () => { setShowLoginModal(false); setShowRegisterModal(false); setShowForgotModal(true); };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>ProposAI — AI-Powered RFP Platform for Canadian Businesses | Bilingual EN/FR</title>
        <meta name="description" content="Create professional RFPs in minutes with AI, match with qualified Canadian vendors, and make confident procurement decisions. Fully bilingual EN/FR, PIPEDA-compliant, built in Canada. Free to start." />
        <meta name="keywords" content="RFP software Canada, best RFP software, AI RFP generator, procurement software Canada, RFP management tool, Loopio alternative, Bonfire alternative, RFP template Canada, vendor management software Canada, PIPEDA compliant procurement, bilingual procurement EN/FR, appel d'offres logiciel, logiciel approvisionnement Canada, procurement automation, RFP evaluation software" />
        <link rel="canonical" href="https://proposai.ca/landing-page" />
        <meta property="og:title" content="ProposAI — AI-Powered RFP Platform for Canadian Businesses" />
        <meta property="og:description" content="Create professional RFPs with AI, match with qualified vendors, and make confident procurement decisions. Bilingual EN/FR, PIPEDA-compliant." />
        <meta property="og:url" content="https://proposai.ca/landing-page" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={i18n.language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta property="og:locale:alternate" content={i18n.language === 'fr' ? 'en_CA' : 'fr_CA'} />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(appJsonLd)}</script>
      </Helmet>

      <LandingNav onLoginClick={openLogin} onRegisterClick={openRegister} />

      <LandingHero
        onRegisterAsBusinessClick={() => openRegisterAs('business')}
        onRegisterAsVendorClick={() => openRegisterAs('vendor')}
      />
      <LandingHowItWorks />
      <LandingFeatures />
      <LandingBenefits onRegisterClick={openRegister} />
      <LandingPractices />
      <LandingPricing
        onRegisterClick={openRegister}
        onEnterpriseClick={() => setShowEnterpriseModal(true)}
      />
      <VendorMarketplaceSection onRegisterClick={() => openRegisterAs('vendor')} />
      <TestimonialsSection />
      <FAQSection />
      <FinalCTASection onRegisterClick={openRegister} onLoginClick={openLogin} />
      <TrustBanner />
      <FooterSection />

      <EnterpriseContactModal open={showEnterpriseModal} onOpenChange={setShowEnterpriseModal} />

      <LandingLoginModal
        open={showLoginModal}
        onOpenChange={setShowLoginModal}
        onForgotClick={openForgot}
        onRegisterClick={openRegister}
      />
      <LandingForgotModal
        open={showForgotModal}
        onOpenChange={setShowForgotModal}
        onLoginClick={openLogin}
      />
      <LandingRegisterModal
        open={showRegisterModal}
        onOpenChange={setShowRegisterModal}
        onLoginClick={openLogin}
        initialRole={registerRole}
      />
    </div>
  );
};

export default LandingNew;
